import { country } from '@theme/localization-detect';
import { containScroll } from '@theme/contained-scroll';

/**
 * One list of the localization form: a summary that shows the current
 * choice and a panel of submit buttons, one per option.
 *
 * Without this file the list still works: the options are submit buttons of
 * the localization form and the panel is a details element.
 */
/* The share of the screen an open list may take, the same one snippets/localization-list.liquid holds. */
const SHARE_OF_SCREEN = 0.6;

class ChoiceList extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('[data-choice-details]');
    this.summary = this.querySelector('[data-choice-summary]');
    this.panel = this.querySelector('[data-choice-panel]');
    if (!this.details || !this.summary) return;

    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('click', this.onClick);
    this.details.addEventListener('toggle', this.onToggle);
    document.addEventListener('click', this.onDocumentClick);

    /* An open list scrolls on its own, and the page under it stays put, also with nothing to scroll. */
    this.abort = new AbortController();
    if (this.panel) containScroll(this.panel, { signal: this.abort.signal, always: true });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    this.abort?.abort();
  }

  /** The buttons, in the order they are printed. */
  get options() {
    return Array.from(this.querySelectorAll('[data-choice-option]'));
  }

  /**
   * Opening moves focus into the list only when it was opened from the
   * keyboard: taking focus off the summary under a pointer draws a ring
   * nobody asked for. A summary opened by a pointer does not match
   * :focus-visible, a summary opened from the keyboard does.
   */
  onToggle = () => {
    if (!this.details.open) return;
    this.place();
    if (!this.summary.matches(':focus-visible')) return;
    this.current()?.focus();
  };

  /** Which way the list opens and how tall it may be: the side with more room, and never more than the share the stylesheet allows. */
  place() {
    if (!this.panel) return;
    const gap = 8;
    const rect = this.summary.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - gap;
    const above = rect.top - gap;
    this.panel.style.maxHeight = '';
    const wanted = this.panel.scrollHeight;
    const up = wanted > below && above > below;
    this.classList.toggle('choice-list--up', up);
    this.classList.toggle('choice-list--down', !up);
    const room = Math.max(0, Math.round(up ? above : below));
    const limit = Math.min(room, Math.round(window.innerHeight * SHARE_OF_SCREEN));
    this.panel.style.maxHeight = wanted > limit ? `${limit}px` : '';
  }

  /** The option the store is set to, or the first one. */
  current() {
    const options = this.options;
    return options.find((option) => option.getAttribute('aria-current') === 'true') || options[0];
  }

  close(focusSummary) {
    if (!this.details.open) return;
    this.details.open = false;
    if (focusSummary) this.summary.focus();
  }

  /**
   * The row for a market shown as one: it posts the country the platform
   * places the visitor in by address. The region panel does this itself.
   */
  onClick = async (event) => {
    const row = event.target.closest('[data-choice-detect]');
    if (!row || row.closest('localization-panel')) return;
    if (!row.form) return;
    event.preventDefault();
    const code = await country();
    if (!code) {
      this.close(true);
      return;
    }
    const name = this.querySelector('[data-choice-option][name]')?.name;
    if (!name) return;
    row.name = name;
    row.value = code;
    row.form.requestSubmit(row);
  };

  onDocumentClick = (event) => {
    if (this.contains(event.target)) return;
    this.close(false);
  };

  onKeydown = (event) => {
    if (event.key === 'Escape') {
      /* Esc on an open list closes the list only: the panel around it stays. */
      if (this.details.open) event.stopPropagation();
      this.close(true);
      return;
    }
    if (!this.details.open) return;

    const options = this.options;
    if (!options.length) return;
    const step = { ArrowDown: 1, ArrowUp: -1 }[event.key];
    const edge = { Home: 0, End: options.length - 1 }[event.key];

    if (step === undefined && edge === undefined) return;
    event.preventDefault();

    if (edge !== undefined) {
      options[edge].focus();
      return;
    }

    const at = options.indexOf(document.activeElement);
    if (at === -1) {
      this.current()?.focus();
      return;
    }
    const next = Math.min(options.length - 1, Math.max(0, at + step));
    options[next].focus();
  };
}

if (!customElements.get('choice-list')) {
  customElements.define('choice-list', ChoiceList);
}
